import { useState, useRef, useEffect, useMemo } from 'react';
import { chunk } from 'lodash';
import styles from 'widget/event_components/ticket-spot.scss';
import { CSSTransition } from 'react-transition-group';
import { Col, Row, Form } from 'react-bootstrap';
import cn from 'classnames';
import * as E from 'widget/event_components/Ticket/elements';
import { filterConnectedTickets } from 'widget/event_components/Ticket/utils';
import { QuestionType } from 'widget/event_components/Ticket/components/Question';
import { ConfirmationIframe } from 'widget/event_components/Ticket/components/ConfirmationIframe';

export const TicketForm = ({
  attendeeIndex,
  ticketId,
  isWaitList,
  confirmMessage,
  orderConfirmationUrl,
  session = {},
  event,
  settings,
  complete,
  errors = {},
  setCustomField,
  setField,
  form = {},
  cart = {},
  tickets,
  onResizeComponent,
}) => {
  const [singleLineFields, setSingleLineFields] = useState(false);
  const ticketsIdToUUID = tickets.reduce((res, curr) => ({ ...res, [curr.get('id')]: curr.get('ticket_uuid') }), {});
  const ticketsInCart = Object.keys(cart).map((cartTicket) => ticketsIdToUUID[cartTicket]);

  const showAttendeeInputsPerTicket = settings.get('showAttendeeInputsPerTicket');
  const showConfirmEmail = settings.get('showConfirmEmail');
  const rowRef = useRef(null);
  const hasFullScreenLayoutLayout = (_settings) => ['list', 'list2'].some((layout) => layout === _settings.get('layoutDesign'));

  const ticket = tickets.find((t) => +t.get('id') === +ticketId);
  const questions = filterConnectedTickets(event, showAttendeeInputsPerTicket, ticketsInCart, ticket ? ticket.get('ticket_uuid') : null);
  const questionsChunk = chunk(questions, 2);

  useEffect(() => {
    if (rowRef.current) {
      const rowWidth = rowRef.current.offsetWidth;
      setSingleLineFields(rowWidth <= 300);
      onResizeComponent && onResizeComponent(true);
    }
  }, []);

  const questionsComponents = useMemo(
    () => (
      <>
        {questionsChunk.map((questionChunk) => (
          <Row>
            {questionChunk
              .filter((question) => !(isWaitList === true && question.excludeInWaitlist))
              .map((question) => {
                const isCheckbox = question.type === 'checkbox';
                const questionTypeComponent = (
                  <QuestionType
                    errors={errors}
                    setCustomField={setCustomField}
                    question={question}
                    form={form}
                    event={event}
                    label={isCheckbox ? question.question : null}
                  />
                );
                const questionLabelComponent = <Form.Label className="checkout-form-field">{question.question}</Form.Label>;
                return (
                  <Col xs={hasFullScreenLayoutLayout(settings) ? 12 / questionChunk.length : 12}>
                    <E.CustomFields className={cn('mb-3')}>
                      {questionLabelComponent}
                      {questionTypeComponent}
                    </E.CustomFields>
                  </Col>
                );
              })}
          </Row>
        ))}
      </>
    ),
    [questionsChunk, errors, form, event, setCustomField]
  );

  const ticketName = tickets.find((t) => +t.get('id') === +ticketId)?.get('name');
  return (
    <div className={styles.ticketContainer} ref={rowRef}>
      {/* {(isWaitlist || event.get('series_id')) && event.get('start_date') && (
            <p className={`${styles.date} checkout-pre-text`}>
              {moment(event.get('start_date')).tz(event.get('timezone')).format(settings.get('dateFormat'))}
              {' - '}
              {moment(event.get('start_date')).tz(event.get('timezone')).format(settings.get('timeFormat'))}
            </p>
          )} */}
      {attendeeIndex && ticketName && showAttendeeInputsPerTicket && (
        <div className="checkout-form-attendee-count-label">
          {settings.get('checkoutForAttendeeCountLabel')} {attendeeIndex} - {ticketName}
        </div>
      )}
      <CSSTransition classNames="checkout-form-success" timeout={200} in={complete}>
        <Col xs={12}>
          <div
            className={cn(styles.success, 'checkout-success-text', {
              [styles.checkoutFormVisible]: complete,
              [styles.checkoutFormInvisible]: !complete,
            })}
          >
            {complete && orderConfirmationUrl && (
              <div className={`${styles.ticketsSuccess} checkout-success-text`}>
                <ConfirmationIframe url={orderConfirmationUrl} onResizeComponent={onResizeComponent} />
              </div>
            )}
            {complete && !orderConfirmationUrl && (
              <div
                className={`${styles.ticketsSuccess} checkout-success-text`}
                dangerouslySetInnerHTML={{
                  __html: confirmMessage,
                }}
              />
            )}
          </div>
        </Col>
      </CSSTransition>

      <CSSTransition classNames={styles.checkoutForm} timeout={200} in={!complete} unmountOnExit>
        <Col xs={12} style={{ paddingBottom: 20 }}>
          <p className={cn(styles.center, 'checkout-pre-text')}>
            {isWaitList ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: event.get('waitlist_description'),
                }}
              />
            ) : (
              settings.get('checkoutPreText')
            )}
          </p>
          <Row>
            <Col xs={singleLineFields ? 12 : 6}>
              <E.CustomFields className="mb-3">
                <Form.Label className="checkout-form-field" required>
                  {settings.get('checkoutFormFirstName')}
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="John"
                  required
                  onChange={(e) => setField('first_name', e.target.value)}
                  isInvalid={!!errors.first_name}
                  defaultValue={form?.first_name || ''}
                />
                <Form.Control.Feedback type="invalid">{errors.first_name}</Form.Control.Feedback>
              </E.CustomFields>
            </Col>
            <Col xs={singleLineFields ? 12 : 6}>
              <E.CustomFields className="mb-3">
                <Form.Label className="checkout-form-field">{settings.get('checkoutFormLastName')}</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Smith"
                  required
                  onChange={(e) => setField('last_name', e.target.value)}
                  isInvalid={!!errors.last_name}
                  defaultValue={form?.last_name || ''}
                />
                <Form.Control.Feedback type="invalid">{errors.last_name}</Form.Control.Feedback>
              </E.CustomFields>
            </Col>
          </Row>

          <Row>
            <Col xs={!showConfirmEmail || singleLineFields ? 12 : 6}>
              <E.CustomFields className="mb-3">
                <Form.Label className="checkout-form-field">{settings.get('checkoutFormEmail')}</Form.Label>
                <Form.Control
                  type="email"
                  required
                  placeholder="example@ticketspot.io"
                  onChange={(e) => setField('email', e.target.value)}
                  isInvalid={!!errors.email}
                  disabled={session.attendee}
                  defaultValue={form?.email || ''}
                />
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </E.CustomFields>
            </Col>
            {showConfirmEmail ? (
              <Col xs={singleLineFields ? 12 : 6}>
                <E.CustomFields className="mb-3">
                  <Form.Label className="checkout-form-field">Confirm {settings.get('checkoutFormEmail')}</Form.Label>
                  <Form.Control
                    type="email"
                    required
                    placeholder="example@ticketspot.io"
                    onChange={(e) => setField('confirm_email', e.target.value)}
                    isInvalid={!!errors.confirm_email}
                    disabled={session.attendee}
                    defaultValue={form?.confirm_email || ''}
                  />
                  <Form.Control.Feedback type="invalid">{errors.confirm_email}</Form.Control.Feedback>
                </E.CustomFields>
              </Col>
            ) : (
              <span />
            )}
          </Row>
          {questionsComponents || <span />}
        </Col>
      </CSSTransition>
    </div>
  );
};
